<template>
    <ModalBase
        name="invitaciones-copiadas"
        title="Invitaciones generadas"
        subtitle="Revisa los correos de destino y envía las invitaciones."
        :width="720"
        @close="$emit('close')"
    >
        <div class="tw:flex tw:flex-col tw:gap-5">
            <div class="tw:flex tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:border-emerald-200 tw:bg-emerald-50 tw:p-3">
                <i class="fas fa-check-circle tw:mt-0.5 tw:text-emerald-600" aria-hidden="true"></i>
                <p class="tw:m-0 tw:text-sm tw:text-emerald-800">
                    Todas las entradas de los pedidos se han generado correctamente.
                </p>
            </div>

            <div v-if="orders.length" class="tw:flex tw:flex-col tw:gap-4">
                <div v-for="order in orders" :key="order.id">
                    <p class="tw:mb-1 tw:text-sm tw:font-medium tw:text-gray-700">
                        {{ etiquetaPedido(order) }}
                    </p>

                    <BaseInputText
                        :id="`email-invitacion-${order.id}`"
                        type="email"
                        label="email"
                        rules="required|email"
                        :name="`email-invitacion-${order.id}`"
                        :value="emails[order.id]"
                        :disabled="loading"
                        :api-validation-error="errores[order.id]"
                        :data-cy="`email-invitacion-${order.id}`"
                        @input="value => setEmail(order.id, value)"
                    />
                </div>
            </div>

            <div class="tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:border-gray-200 tw:p-4">
                <div class="tw:flex tw:items-start tw:justify-between tw:gap-4">
                    <div>
                        <p class="tw:m-0 tw:text-sm tw:font-medium tw:text-gray-800">
                            Caducidad de las invitaciones
                        </p>
                        <p class="tw:m-0 tw:text-xs tw:text-gray-500">
                            Si la activas, las entradas dejan de ser válidas a partir de la fecha indicada.
                        </p>
                    </div>

                    <ToggleSwitch
                        :value="caducidad_entradas"
                        aria-label="Activar caducidad de las invitaciones"
                        data-cy="toggle-caducidad"
                        @input="onCaducidad"
                    />
                </div>

                <BaseDateTimeInput
                    v-if="caducidad_entradas"
                    name="fecha_caducidad"
                    label="Fecha de caducidad"
                    rules="required"
                    :value="fecha_caducidad"
                    :min="minFecha"
                    :disabled="loading"
                    :api-validation-error="fechaError"
                    data-cy="fecha-caducidad"
                    @input="onFecha"
                />

                <p v-if="caducidad_entradas" class="tw:m-0 tw:text-xs tw:text-gray-500">
                    Se guarda en hora de Madrid (Europe/Madrid); el campo muestra la hora local de tu equipo.
                </p>
            </div>
        </div>

        <template #footer>
            <div class="tw:flex tw:items-center tw:justify-end tw:gap-2">
                <Button variant="ghost" :disabled="loading" @click="$emit('close')">
                    Cerrar
                </Button>
                <Button
                    variant="primary"
                    :loading="loading"
                    data-cy="enviar-invitaciones"
                    @click="enviarInvitaciones"
                >
                    Enviar invitaciones
                </Button>
            </div>
        </template>
    </ModalBase>
</template>

<script setup>
    import { computed } from 'vue';
    import { useToast } from '@/composables/common/useToast';
    import { useEnviarInvitaciones } from '@/composables/admin/events/sesiones/useEnviarInvitaciones';

    import ModalBase from '@/modules/admin/ui/ModalBase.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue';
    import BaseDateTimeInput from '@/modules/admin/ui/BaseDateTimeInput.vue';
    import ToggleSwitch from '@/modules/admin/ui/ToggleSwitch.vue';

    const props = defineProps({
        orders: {
            type: Array,
            default: () => []
        },
        eventoId: {
            type: [Number, String],
            required: true
        },
        sesionId: {
            type: [Number, String],
            required: true
        }
    });

    const emit = defineEmits(['close']);
    const toast = useToast();

    const {
        emails,
        errores,
        loading,
        caducidad_entradas,
        fecha_caducidad,
        fechaError,
        setEmail,
        enviarInvitaciones: enviar
    } = useEnviarInvitaciones({
        orders: props.orders,
        eventoId: props.eventoId,
        sesionId: props.sesionId
    });

    // `min` del datetime-local: ahora mismo, para no poder caducar en el pasado.
    const minFecha = computed(() => {
        const now = new Date();
        const pad = value => String(value).padStart(2, '0');
        return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    });

    /**
     * Cabecera de cada campo: "Pedido 123 - 2 entradas". El concepto del ticket
     * no va acá: el usuario pidió sólo pedido y número de entradas, y así la
     * cabecera no se corta con conceptos largos.
     */
    const etiquetaPedido = order => {
        const total = (order.qr_codes || []).length;
        return `Pedido ${order.id} - ${total} ${total === 1 ? 'entrada' : 'entradas'}`;
    };

    function onCaducidad(value) {
        caducidad_entradas.value = value;
    }

    function onFecha(value) {
        fecha_caducidad.value = value;
    }

    async function enviarInvitaciones() {
        const result = await enviar();

        if (result.ok) {
            if (result.message) toast.success(result.message);
            emit('close');
            return;
        }

        if (result.error) {
            console.error('Error enviando invitaciones:', result.error);
            toast.error('No se han podido enviar las invitaciones');
        }
    }
</script>
